/**
 * API Client for Image Collection ("Bildersammlung")
 *
 * Provides access to current + historical product images with filtering.
 */
import { apiClient } from '@/lib/api-client';
import type {
  ImageCollectionResponse,
  FilterOptionsResponse,
  CollectionFilters,
  VersionHistoryResponse,
  RollbackResponse,
} from '@/types/image-collection';

const API_BASE = '/api/v1';

/**
 * Lädt die Image Collection eines Produkts (current + historical)
 */
export async function getImageCollection(
  productId: string,
  filters?: CollectionFilters
): Promise<ImageCollectionResponse> {
  const params = new URLSearchParams();

  if (filters?.tags?.length) params.append('tags', filters.tags.join(','));
  if (filters?.source) params.append('source', filters.source);
  if (filters?.channel) params.append('channel', filters.channel);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.page) params.append('page', filters.page.toString());
  if (filters?.per_page) params.append('per_page', filters.per_page.toString());

  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE}/products/${productId}/images/collection?${queryString}`
    : `${API_BASE}/products/${productId}/images/collection`;

  const response = await apiClient.get<ImageCollectionResponse>(url);
  return response.data;
}

/**
 * Lädt verfügbare Filter-Optionen für ein Produkt
 */
export async function getImageCollectionFilters(
  productId: string
): Promise<FilterOptionsResponse> {
  const response = await apiClient.get<FilterOptionsResponse>(
    `${API_BASE}/products/${productId}/images/collection/filters`
  );
  return response.data;
}

/**
 * Lädt die Version History eines Product Asset Links
 */
export async function getImageVersions(
  productId: string,
  linkId: string,
  limit?: number
): Promise<VersionHistoryResponse> {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());

  const queryString = params.toString();
  const url = queryString
    ? `${API_BASE}/products/${productId}/images/${linkId}/versions?${queryString}`
    : `${API_BASE}/products/${productId}/images/${linkId}/versions`;

  const response = await apiClient.get<VersionHistoryResponse>(url);
  return response.data;
}

/**
 * Rollt ein Bild zu einer früheren Version zurück
 */
export async function rollbackImageVersion(
  productId: string,
  linkId: string,
  versionId: string
): Promise<RollbackResponse> {
  const response = await apiClient.post<RollbackResponse>(
    `${API_BASE}/products/${productId}/images/${linkId}/versions/${versionId}/rollback`,
    {}
  );
  return response.data;
}
